import REMOTE_ASSETS from '@/constants/REMOTE_ASSETS';

// Building Fund 2026 digital pledge (BF2026).
//
// The whole feature — the landing-page banner and every pledge screen behind it —
// sits behind this one flag so the code can ship in a native binary while the
// campaign is still being prepared. Create the row off in production and flip it
// when the pledge window opens; delete the flag once the campaign is over.
export const BUILDING_FUND_PLEDGE_FLAG = 'building-fund-pledge';

// The pledge window. ON: pledges can be submitted, and a Building Fund FAST gift
// goes straight to the BF2026 "FAST - Submitted" screen. OFF: the form shows with
// Continue disabled, Confirm sends nothing, and a Building Fund FAST gift gets the
// ordinary success screens. Pledges already made, My pledge and My BF Fulfilment
// stay as they are either way.
//
// An absent row reads as OFF, i.e. CLOSED: an environment needs this row switched
// on before a release reaches it, or pledging closes there.
export const BF_PLEDGE_OPEN_FLAG = 'bf-pledge-open';

// The submit celebration (Daryl's T1 and D2b, 11 Oct): the slider's fill and heart,
// the "My Privilege to Build" video before the submitted page, and the "My First"
// stamp landing on a first-timer's banner. OFF, or no row: the pages as before.
// Delete the flag, and the OFF branches, once it is on everywhere.
export const BF_PLEDGE_CELEBRATION_FLAG = 'bf-pledge-celebration';

// Told to a member whose Submit lands after the window closed.
export const BF_PLEDGE_CLOSED_MESSAGE = 'The BF2026 pledge is not open right now.';

export const BUILDING_FUND_PLEDGE_PATH = '/my-giving/building-fund-pledge';

// The campaign artwork ("My Privilege to Build"). The pledge pages' banner (800x800,
// building centred, runs to the top edge under the status bar) is from the D2 frame
// export of 30 Sep 2026; the MyGiving landing
// banner has its own crop (1005x483, 3x its 335x161 slot) with the building on the
// left, leaving the right side clear for the pill. Both from the design canvas.
export const BF_PLEDGE_BANNER = '/assets/images/my-giving/building-fund/bf2026-banner.jpg';
// A first-timer's submitted page (D1b–D4b, the canvas's 15 Oct 2026 frames): the
// embroidered patch, two star clusters and the "My 1st K" badge on a woven paper
// ground, flattened from the frame's layers into one picture at 2x (750x1010): the
// square banner, then 130px of paper for the heading band to sit on. On the CDN,
// not in the bundle, like the other campaign artwork.
export const BF_FIRST_ARTWORK = REMOTE_ASSETS.bfFirstTimerArtwork;
// The celebration's video (7.2s, 750x1624, no sound), and its last frame for when it
// cannot play. An MP4, not an animated WebP like MyGiving's: at this length a WebP
// comes out 10-20x the size.
export const BF_PLEDGE_SUBMITTED_VIDEO = '/assets/videos/bf2026-pledge-submitted.mp4';
export const BF_PLEDGE_SUBMITTED_STILL = '/assets/videos/bf2026-pledge-submitted-still.webp';
// The top edge of the paper ground (its average, the weave is not flat), for the status
// bar Android paints above it.
export const BF_PLEDGE_BANNER_FIRST_TOP = '#DAD9D9';

// There is no first page (P0) any more: the bare path redirects to the form. A
// five-month or sow pledge is checked on `confirm` (C1, C2) before it is sent; a
// one-time pledge has no confirm page.
export const BF_PLEDGE_ROUTES = {
  form: `${BUILDING_FUND_PLEDGE_PATH}/form`,
  confirm: `${BUILDING_FUND_PLEDGE_PATH}/confirm`,
  done: `${BUILDING_FUND_PLEDGE_PATH}/done`,
  view: `${BUILDING_FUND_PLEDGE_PATH}/view`,
};

// A one-time pledge goes on to the existing Give now flow with this fund
// preselected. Must match an entry in giving-options.vue's `funds` list.
export const BF_GIVE_NOW_FUND = 'Building Fund';

// A successful one-time FAST gift to BF_GIVE_NOW_FUND gets the campaign's "FAST -
// Submitted" screen in place of the generic one; this is its "Giving to" line, as
// the frame words it (giving-api's category says only "Building Fund").
export const BF_FAST_GIVING_TO = 'Building Fund 2026';

// The campaign the pledge form collects for, and its five months in order. `key` is
// the field name in the pledge API's `bf` object; `label` is the chip over each input;
// `period` is the month as GET /bf/fulfilment names it.
export const BF_PLEDGE_YEAR = 2026;
export const BF_PLEDGE_MONTHS = [
  { key: 'month_1_commitment', label: "OCT'26", period: '2026-10' },
  { key: 'month_2_commitment', label: "NOV'26", period: '2026-11' },
  { key: 'month_3_commitment', label: "DEC'26", period: '2026-12' },
  { key: 'month_4_commitment', label: "JAN'27", period: '2027-01' },
  { key: 'month_5_commitment', label: "FEB'27", period: '2027-02' },
];

// A pledge card's icon: the frames' emoji on a tinted circle. Shared by the pledge
// cards (PledgeCardTitle) and the fulfilment cards (FulfilmentCard).
export const BF_PLEDGE_CARD_TONES = {
  oneTime: { circle: 'bg-campaign-highlight', emoji: '🤲🏻' },
  months: { circle: 'bg-campaign-highlight', emoji: '🗓️' },
  sow: { circle: 'bg-campaign-growth', emoji: '🌱' },
  given: { circle: 'bg-campaign-highlight', emoji: '🎁' },
  // The FAST screen's heart (an SVG, through the icon slot). Nearest tokens to the
  // frame's #FDE3E7 / #E0445A.
  gift: { circle: 'bg-status-errorLite text-status-error', emoji: '' },
};

// The tick a youth sees on the form, and the line a first-timer's pledge shows
// (frames P1a/P1b, C1, D1b–D4b). The copy is the frames'; giving-api stores the
// answer as `first_four_digit_pledge` (contract §5.4, copy not final there).
export const BF_FIRST_TIME_LABEL = "I'm giving for the first time";
// The field means a first four-digit pledge, so the tick is asked only from this
// amount: the one-time amount, or the five months added up.
export const BF_FIRST_TIME_MIN_AMOUNT = 1000;

// A youth may pledge more than this a month with parental consent, so going over it
// is flagged, never blocked. Keep giving-mode.vue's Building Fund note in step.
export const BF_YOUTH_MONTHLY_CAP = 200;

// Shown to a youth over the cap, on the form and on D. A one-time gift is given
// within a month, so "a month" holds for it too.
export const BF_YOUTH_NOTICE = `Amounts above $${BF_YOUTH_MONTHLY_CAP} a month need parent/guardian approval.`;

// A member this age or younger, by birth year, pledges as a youth whether or not
// they tick the box, as the web pledge form does. Its `ageIsAbove18` is misnamed:
// building-fund-api computes it as `YEAR(NOW()) - YEAR(dob) > 16`, which matches
// the tick's own "aged 16 and below".
export const BF_YOUTH_MAX_AGE = 16;

// The landing-page banner is for members this age or older, by birth year, as
// giving-api decides `ageIsAbove18` for the tithing and BF history tiles
// (`YEAR(NOW()) - YEAR(dob) >= 18`), so the banner and the tiles agree.
export const BF_BANNER_MIN_AGE = 18;

// The largest amount the pledge API accepts: giving-api's `MAX_COMMITMENT` in
// pledge-body.dto.ts is 10^8 - 1, a whole number, so 99,999,999.50 is refused.
export const BF_PLEDGE_AMOUNT_MAX = 10 ** 8 - 1;

export const BF_PLEDGE_PRIVACY_URL = 'https://privacy.heartofgodchurch.org';

export const BF_PLEDGE_ERRORS = {
  // Same wording as the other amount fields in MyGiving (paynow, eGIRO transfer).
  amount: 'Please enter a number',
  // The partner fields' other messages are the app's shared ones
  // (src/constants/validation/error-messages.js); this one has no constant there,
  // so it is RegisterBio.vue's wording.
  dobFuture: 'Date cannot be in the future',
};

// giving-api's limit on the sow text (`SOW_PLEDGE_MAX_LENGTH`, pledges.sow_pledge
// VARCHAR(500)). Longer text is a 400 whose message is not written for members.
export const BF_SOW_MAX_LENGTH = 500;

// The pledge routes' errors that are written for members, verbatim from giving-api's
// pledge-write.service.ts. Those routes answer every failure as a 400 with the
// message in `errors[0]` — a server fault's raw text too — so only these are shown.
export const BF_PLEDGE_API_MESSAGES = {
  ALREADY_CONFIRMED:
    'Creation of Another Confirmed Pledge not allowed as Pledge already confirmed. Please contact Accounts if you want to revise your pledge.',
  ONE_TIME_OR_MONTHS: 'Please pledge either a one-time amount or over the 5 months, not both.',
  // A member whose profile has no personal ID (giving-api #203): told why.
  NO_PERSONAL_ID:
    "Your profile doesn't have your NRIC/FIN details yet, so we can't record your pledge. Please add them in your HOGC One profile and try again once they're approved.",
};

// Shown if saving "My Dreams & Prayers" fails.
export const BF_DREAMS_SAVE_FAILED = 'Failed to save your Dreams & Prayers. Please try again.';
